"use client";

import { FunctionComponent, ReactNode } from "react";
import { TailwindColors } from "../types/general";
import { Body, Buttons, Headline, Icon, Text, Wrapper } from "./inline-message.styles";
import { getIconByType } from "./inline-message.utils";
import { Button, ButtonProps } from "../button/button";

export interface InlineMessageProps {
    /**
     * Type of the inline message.
     */
    type?: "error" | "warning" | "info" | "success";

    /**
     * Headline of the inline message.
     */
    headline?: string;

    /**
     * Alignment of the inline message.
     */
    align?: "left" | "center" | "right";

    /**
     * Text of the inline message.
     */
    text?: string;

    /**
     * Color of the inline message. Will only be applied if `type` is not set.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Show icon of the inline message.
     */
    showIcon?: boolean;

    /**
     * Icon of the inline message. Will only be applied if `type` is not set.
     */
    icon?: ReactNode;

    /**
     * Buttons of the inline message.
     */
    buttons?: ButtonProps[];

    /**
     * Class name of the inline message.
     */
    className?: string;
}

export const InlineMessage: FunctionComponent<InlineMessageProps> = ({
    type,
    headline,
    align = "center",
    text,
    color,
    showIcon = true,
    icon,
    buttons,
    className,
}) => {
    return (
        <Wrapper align={align} className={className}>
            {showIcon && (
                <Icon type={type} color={color} align={align}>
                    {!type && icon ? icon : getIconByType(type)}
                </Icon>
            )}
            <Body>
                <Headline>{headline || "Error!"}</Headline>
                <Text>{text || "Something went wrong. Please try again later."}</Text>
            </Body>
            {buttons && (
                <Buttons align={align}>
                    {buttons.map((button, index) => (
                        <Button size="xs" rounded="full" key={index} {...button} />
                    ))}
                </Buttons>
            )}
        </Wrapper>
    );
};
